import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export interface SettingsNavItem {
  id: string;
  label: string;
  /** One-line descriptor shown under the label. */
  description: string;
  icon: ReactNode;
}

export interface SettingsNavProps {
  items: SettingsNavItem[];
  activeId: string;
  onSelect: (id: string) => void;
}

/**
 * Vertical section switcher for a settings-style page. The active row lifts off
 * the page into a white card with a 3px orange spine — the light-surface cousin
 * of the app sidebar's selection line — so the current section reads at a
 * glance. Inactive rows stay quiet until hovered.
 */
export function SettingsNav({ items, activeId, onSelect }: SettingsNavProps) {
  return (
    <nav aria-label="Settings sections" className="flex flex-col gap-1.5 lg:w-[248px] lg:shrink-0">
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            aria-current={active ? 'page' : undefined}
            data-testid={`settings-nav-${item.id}`}
            className={cn(
              'group relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-150',
              active ? 'bg-card shadow-sm ring-1 ring-grey-200' : 'hover:bg-card/60'
            )}
          >
            {/* Orange spine — mirrors the dark sidebar's 3px selection line, inset so
                it floats inside the card's rounded corners. */}
            <span
              aria-hidden="true"
              className={cn(
                'absolute inset-y-[10px] left-0 w-[3px] rounded-full bg-dawn-orange transition-opacity duration-150',
                active ? 'opacity-100' : 'opacity-0'
              )}
            />
            <span
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-150',
                active
                  ? 'bg-dawn-orange text-white'
                  : 'bg-grey-100 text-grey-400 group-hover:bg-orange-100 group-hover:text-dawn-orange'
              )}
            >
              {item.icon}
            </span>
            <span className="min-w-0 flex-1">
              <span
                className={cn(
                  'block truncate text-[13px] leading-tight',
                  active ? 'font-semibold text-dawn-black' : 'font-medium text-dawn-black/80'
                )}
              >
                {item.label}
              </span>
              <span className="mt-0.5 block truncate text-[11px] leading-tight text-grey-400">
                {item.description}
              </span>
            </span>
            <ChevronRight
              aria-hidden="true"
              className={cn(
                'h-4 w-4 shrink-0 transition-all duration-150',
                active
                  ? 'translate-x-0 text-dawn-orange opacity-100'
                  : '-translate-x-1 text-grey-300 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
              )}
            />
          </button>
        );
      })}
    </nav>
  );
}
